import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Screen from '../../components/common/Screen';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Button from '../../components/elements/Button';
import Icon from '../../components/common/Icon';
import Image from '../../components/images/Image';
import Modal from '../../components/elements/Modal';
import Toast from '../../components/forms/Toast';
import { fetchUserInfo, apiRequest } from '../../utils';
import TeamPaymentBanner, { useTeamPaymentLock } from '../../components/team/TeamPaymentLock';

const PRIMARY = 'var(--primary-color, #0d6efd)';

const styles = {
  capacityBar: {
    height: '8px',
    width: '100%',
    background: '#e5e7eb',
    borderRadius: '999px',
    overflow: 'hidden',
    marginTop: '0.25rem',
  },
  capacityFill: (pct) => ({
    height: '100%',
    width: `${pct}%`,
    background: PRIMARY,
    transition: 'width 280ms ease',
  }),
  capacityCaption: {
    fontSize: '0.78rem',
    color: '#6b7280',
    fontWeight: 500,
    textAlign: 'right',
    marginTop: '0.2rem',
  },
  sectionLabel: {
    color: '#6b7280',
    fontWeight: 600,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginTop: '1.25rem',
    marginBottom: '0.45rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.5rem',
  },
  playerRow: (highlight) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.7rem',
    padding: '0.55rem 0.7rem',
    background: '#fff',
    border: `1px solid ${highlight ? PRIMARY : '#e5e7eb'}`,
    borderRadius: '12px',
    marginBottom: '0.5rem',
    transition: 'border-color 180ms ease, background 180ms ease',
  }),
  avatarWrap: {
    position: 'relative',
    flex: '0 0 auto',
    width: '2.6rem',
    height: '2.6rem',
  },
  captainChip: {
    position: 'absolute',
    bottom: '-3px',
    right: '-3px',
    minWidth: '1.15rem',
    height: '1.15rem',
    padding: '0 2px',
    borderRadius: '999px',
    background: PRIMARY,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.65rem',
    border: '2px solid #fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
  },
  nameBlock: {
    flex: '1 1 auto',
    minWidth: 0,
    textAlign: 'left',
    overflow: 'hidden',
  },
  nameLink: {
    textDecoration: 'none',
    color: 'inherit',
    display: 'block',
  },
  name: {
    fontWeight: 600,
    color: '#1f2937',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '0.95rem',
  },
  email: {
    fontSize: '0.78rem',
    color: '#6b7280',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  meTag: {
    fontSize: '0.7rem',
    color: '#9ca3af',
    fontStyle: 'italic',
    marginLeft: '0.35rem',
    fontWeight: 500,
  },
  captainPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.2rem',
    marginLeft: '0.4rem',
    padding: '0.05rem 0.45rem',
    borderRadius: '999px',
    background: PRIMARY,
    color: '#fff',
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    verticalAlign: 'middle',
    lineHeight: 1.4,
  },
  iconBtn: (variant) => ({
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: variant === 'danger' ? '1px solid #fecaca' : '1px solid #e5e7eb',
    background: '#fff',
    color: variant === 'danger' ? '#dc2626' : '#374151',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flex: '0 0 auto',
    padding: 0,
    fontSize: '1rem',
    transition: 'transform 120ms ease, background 120ms ease',
  }),
  addPill: {
    padding: '0.42rem 0.85rem',
    borderRadius: '999px',
    border: 'none',
    background: PRIMARY,
    color: '#fff',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    flex: '0 0 auto',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  createPill: {
    padding: '0.3rem 0.7rem',
    borderRadius: '999px',
    border: `1px solid ${PRIMARY}`,
    background: '#fff',
    color: PRIMARY,
    fontSize: '0.74rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    textTransform: 'none',
    letterSpacing: 'normal',
  },
  searchWrap: {
    position: 'relative',
    marginBottom: '0.6rem',
  },
  searchInput: {
    width: '100%',
    height: '44px',
    paddingLeft: '2.4rem',
    paddingRight: '0.9rem',
    borderRadius: '12px',
    border: '1px solid #d1d5db',
    background: '#fff',
    fontSize: '0.95rem',
    outline: 'none',
    boxSizing: 'border-box',
  },
  searchIcon: {
    position: 'absolute',
    left: '0.85rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#9ca3af',
    pointerEvents: 'none',
  },
  fullBanner: {
    padding: '0.7rem 0.9rem',
    background: '#ecfdf5',
    border: '1px solid #a7f3d0',
    color: '#065f46',
    borderRadius: '12px',
    fontWeight: 500,
    fontSize: '0.88rem',
    textAlign: 'center',
    marginBottom: '0.6rem',
  },
  emptyState: {
    padding: '1.1rem 1rem',
    border: '1px dashed #d1d5db',
    borderRadius: '12px',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '0.88rem',
  },
  smallCount: {
    fontSize: '0.72rem',
    color: '#9ca3af',
    fontWeight: 500,
    textTransform: 'none',
    letterSpacing: 'normal',
  },
  inviteCard: {
    padding: '0.8rem 0.9rem',
    borderRadius: '12px',
    background: PRIMARY,
    color: '#fff',
    marginBottom: '0.9rem',
  },
  inviteRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginTop: '0.55rem',
  },
  inviteInput: {
    flex: '1 1 160px',
    minWidth: 0,
    border: 'none',
    borderRadius: '8px',
    padding: '0.45rem 0.6rem',
    fontSize: '0.8rem',
  },
};

const Avatar = ({ url, isCaptain }) => (
  <div style={styles.avatarWrap}>
    <Image
      url={url || localStorage.getItem('defaultPlayerPhoto')
          || require('./../../assets/images/fallback/user.png')}
      size="2.6rem"
      className="circle"
      reload={true}
    />
    {isCaptain && (
      <span style={styles.captainChip} title="Captain">
        <Icon name="user-crown" pack="sr" theme="light" />
      </span>
    )}
  </div>
);

const Page = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [toast, setToast] = useState({ show: false, message: '', icon: 'check' });
  const [busyId, setBusyId] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [confirmPromote, setConfirmPromote] = useState(null);
  // Tapping the ✕ on the captain's own row (when they can't just leave) opens
  // this explainer instead of removing them — you must hand over the crown first.
  const [captainLocked, setCaptainLocked] = useState(false);
  // "Replace" = free this player's seat and email a claim link to a named
  // replacement (a transfer, not a refund). replaceLink holds the shareable link
  // the backend returns so the captain can copy it even if mail delivery is off.
  const [confirmReplace, setConfirmReplace] = useState(null);
  const [replaceEmail, setReplaceEmail] = useState('');
  const [replaceFirst, setReplaceFirst] = useState('');
  const [replaceLink, setReplaceLink] = useState(null);
  const [replaceCopied, setReplaceCopied] = useState(false);
  // "Send a seat" (community leader): hand an open seat on this team to a named
  // person by email — same claim-link mechanics as replace, but nobody is removed.
  const [sendSeatOpen, setSendSeatOpen] = useState(false);
  const [sendSeatEmail, setSendSeatEmail] = useState('');
  const [sendSeatFirst, setSendSeatFirst] = useState('');
  const [sendSeatLink, setSendSeatLink] = useState(null);
  const [sendSeatCopied, setSendSeatCopied] = useState(false);
  const [sendSeatBusy, setSendSeatBusy] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Invite-by-link + approve replace the old "create employee" direct-add: the
  // contact person shares a link, the joiner fills it in, and it lands here as a
  // pending request to accept/decline.
  const [inviteShareLink, setInviteShareLink] = useState(null);
  const [inviteShareCopied, setInviteShareCopied] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);

  useEffect(() => {
    fetchUserInfo('/add-players', setUserInfo, setLoading, setError, []);
  }, []);

  // Open ticket seats + the shareable buy-a-seat link. Only ticket-based team
  // orders ever produce a buy_seat_link (per-seat opt-in on the rate), so on
  // HT/FAB-style projects this stays empty and nothing renders.
  const [sales, setSales] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  useEffect(() => {
    // Pass the team header so a contact person (no team_id on their JWT) gets
    // the sales/invite link for the company team they drilled into.
    const otherTeamId = sessionStorage.getItem('otherTeamId');
    const headers = otherTeamId ? { Otherteamid: otherTeamId } : {};
    apiRequest('GET', '/my-team-sales', null, headers).then(setSales).catch(() => {});
  }, []);
  // A fully-bought team has already-paid open seats → a FREE shareable claim link
  // (friends just join, no payment). A per-seat rate instead produces a PAID
  // buy-a-seat link. When both exist the free one wins — never charge for a seat
  // the captain already bought.
  const inviteFree = !!(sales && sales.claim_seat_link && (sales.open_seats || 0) > 0);
  const inviteLink = inviteFree ? sales.claim_seat_link : (sales && sales.buy_seat_link) || null;
  const inviteOpen = inviteFree
    ? sales.open_seats
    : (sales && sales.buy_seat_link ? Math.max(sales.seats_total - sales.seats_claimed, 0) : 0);
  const copyInvite = () => {
    if (!inviteLink) return;
    // Copy a warm message + link so it's ready to send, not a bare URL.
    navigator.clipboard.writeText(`${t('teamSeatShareText')}\n${inviteLink}`).then(() => {
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2500);
    });
  };

  const players = userInfo?.players || [];
  const employees = userInfo?.employees || [];
  const otherPlayers = userInfo?.other_players || [];
  const maxPlayers = players[0]?.max_players;
  const capacityPct = maxPlayers
    ? Math.min(100, Math.round((players.length / maxPlayers) * 100))
    : 0;
  const isFull = maxPlayers && players.length >= maxPlayers;
  // Admin-side toggle from Machina. Falsey -> roster is read-only here:
  // captains and contact persons still see the current lineup but no
  // promote / remove / add buttons. Default to true when the field is
  // missing (older backend) so we don't accidentally lock the page.
  // Unpaid (an online payment that failed): nobody is added, invited or swapped until it is.
  const payLock = useTeamPaymentLock();
  const canModifyPlayers = userInfo?.can_modify_players !== false && !payLock;
  // Who may create a brand-new colleague account: contact persons (manage the
  // whole company) and the team captain (manages their own roster). Regular
  // players only get to add existing colleagues. The backend enforces the
  // same rule, so this just keeps the button from showing for everyone else.
  const isCaptain = !!players.find((p) => p.id === userInfo?.user_id)?.is_captain;
  const canCreateEmployee = userInfo?.role === 'contactPerson' || isCaptain;
  // Consumer (storefront) teams have no company: there is no colleague pool
  // to add from — seats fill via the invite link — so the whole "add a
  // teammate" area disappears rather than showing an empty search.
  const hasCompanyPool = Boolean(userInfo?.user_company_id);
  // Communities call the roster "members"; companies call them "colleagues".
  const isCommunity = Boolean(userInfo?.is_community);
  const isContact = userInfo?.role === 'contactPerson';
  // A captain may only be removed/replaced from a company or community team; on a
  // plain team someone else must be promoted to captain first (backend enforces it).
  const captainCanLeave = isCommunity || Boolean(userInfo?.company_team);

  const filteredEmployees = useMemo(
    () => employees.filter((e) => (e.full_name || '').toLowerCase().includes(searchQuery)),
    [employees, searchQuery]
  );

  const otherTeamHeader = () => {
    const otherTeamId = sessionStorage.getItem('otherTeamId');
    return otherTeamId ? { Otherteamid: otherTeamId } : {};
  };

  const showToast = (message, icon = 'check') => {
    setToast({ show: true, message, icon });
  };

  const reload = () => {
    fetchUserInfo('/add-players', setUserInfo, setLoading, setError, []);
  };

  const doPromote = async (player) => {
    setBusyId(player.id);
    try {
      await apiRequest('POST', '/update-captain', { playerId: player.id }, otherTeamHeader());
      showToast(`${player.full_name} ${t('isNowCaptain') || 'is now captain'}`, 'user-crown');
      reload();
    } catch (err) {
      showToast(err.message || t('errorOccurred') || 'Something went wrong', 'exclamation');
    } finally {
      setBusyId(null);
      setConfirmPromote(null);
    }
  };

  const doRemove = async (player) => {
    setBusyId(player.id);
    try {
      await apiRequest('POST', '/remove-player', { playerId: player.id }, otherTeamHeader());
      showToast(`${player.full_name} ${t('removed') || 'removed'}`, 'cross');
      reload();
    } catch (err) {
      showToast(err.message || t('errorOccurred') || 'Something went wrong', 'exclamation');
    } finally {
      setBusyId(null);
      setConfirmRemove(null);
    }
  };

  const openReplace = (player) => {
    setReplaceEmail('');
    setReplaceFirst('');
    setReplaceLink(null);
    setReplaceCopied(false);
    setConfirmReplace(player);
  };

  const doReplace = async () => {
    const player = confirmReplace;
    if (!player) return;
    const email = replaceEmail.trim();
    if (!email || !email.includes('@')) {
      showToast(t('invalidEmail') || 'Enter a valid email', 'exclamation');
      return;
    }
    setBusyId(player.id);
    try {
      const res = await apiRequest(
        'POST',
        '/transfer-seat',
        { playerId: player.id, email, first_name: replaceFirst.trim() },
        otherTeamHeader()
      );
      // Stay in the modal on success to surface the claim link (a captain can copy
      // and share it directly — the mail is a convenience, not the only channel).
      setReplaceLink(res?.claim_link || null);
      showToast(`${t('invitationSentTo') || 'Invitation sent to'} ${email}`, 'check');
      reload();
    } catch (err) {
      const map = {
        seatAlreadyUsed: t('seatAlreadyUsed'),
        noTransferableSeat: t('noTransferableSeat'),
        alreadyPlayerInEvent: t('alreadyPlayerInEvent'),
        invalidEmail: t('invalidEmail'),
      };
      showToast(map[err.message] || err.message || t('errorOccurred') || 'Something went wrong', 'exclamation');
    } finally {
      setBusyId(null);
    }
  };

  const copyReplaceLink = () => {
    if (!replaceLink) return;
    navigator.clipboard.writeText(replaceLink).then(() => {
      setReplaceCopied(true);
      setTimeout(() => setReplaceCopied(false), 2000);
    });
  };

  const openSendSeat = () => {
    setSendSeatEmail('');
    setSendSeatFirst('');
    setSendSeatLink(null);
    setSendSeatCopied(false);
    setSendSeatOpen(true);
  };

  const doSendSeat = async () => {
    const email = sendSeatEmail.trim();
    if (!email || !email.includes('@')) {
      showToast(t('invalidEmail') || 'Enter a valid email', 'exclamation');
      return;
    }
    setSendSeatBusy(true);
    try {
      const res = await apiRequest(
        'POST',
        '/company/send-seat',
        { email, first_name: sendSeatFirst.trim() },
        otherTeamHeader()
      );
      // Stay in the modal to surface the paid buy link (a leader can copy + share it
      // even when the event's automatic mails are off). The invitee pays the founding
      // per-seat price on that page — same as a friends-team seat.
      setSendSeatLink(res?.buy_link || null);
      showToast(`${t('seatSent') || 'Invite sent to'} ${email}`, 'check');
      apiRequest('GET', '/my-team-sales', null, otherTeamHeader()).then(setSales).catch(() => {});
      reload();
    } catch (err) {
      const map = {
        noSeatOrder: t('noSeatOrder'),
        teamFull: t('teamFull'),
        alreadyPlayerInEvent: t('alreadyPlayerInEvent'),
        invalidEmail: t('invalidEmail'),
        playerManagementClosed: t('playerManagementClosed'),
      };
      showToast(map[err.message] || err.message || t('errorOccurred') || 'Something went wrong', 'exclamation');
    } finally {
      setSendSeatBusy(false);
    }
  };

  const copySendSeatLink = () => {
    if (!sendSeatLink) return;
    navigator.clipboard.writeText(sendSeatLink).then(() => {
      setSendSeatCopied(true);
      setTimeout(() => setSendSeatCopied(false), 2000);
    });
  };

  const doAdd = async (player) => {
    if (isFull) {
      showToast(t('teamFull') || 'Team is full', 'exclamation');
      return;
    }
    setBusyId(player.id);
    try {
      await apiRequest('POST', '/add-player', { playerId: player.id }, otherTeamHeader());
      showToast(`${player.full_name} ${t('added') || 'added'}`, 'person-circle-plus');
      reload();
    } catch (err) {
      showToast(err.message || t('errorOccurred') || 'Something went wrong', 'exclamation');
    } finally {
      setBusyId(null);
    }
  };

  // Pending join requests (contact persons only — approving is their job).
  const loadPending = () => {
    if (!isContact) return;
    apiRequest('GET', '/join-requests').then((d) => {
      setPendingRequests(d?.requests || []);
    }).catch(() => {});
  };
  useEffect(() => {
    if (userInfo?.role === 'contactPerson') loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.role]);

  // "Add colleague / member" now shares an invite LINK — the joiner fills it in and
  // the contact person approves. No more typing someone in and force-adding them.
  const shareInvite = async () => {
    try {
      const data = await apiRequest('GET', '/company-invite-link', null, otherTeamHeader());
      const link = data?.invite_link;
      if (!link) throw new Error('somethingWentWrong');
      setInviteShareLink(link);
      try {
        // Copy a warm message + link so it's ready to send, not a bare URL.
        await navigator.clipboard.writeText(`${t('colleagueInviteShareText')}\n${link}`);
        setInviteShareCopied(true);
        setTimeout(() => setInviteShareCopied(false), 2500);
        showToast(t('inviteLinkCopied', { defaultValue: 'Invite link copied' }), 'copy');
      } catch { /* clipboard blocked — the link is shown below to copy manually */ }
    } catch (err) {
      showToast(err.message ? (t(err.message) || err.message) : (t('errorOccurred') || 'Something went wrong'), 'exclamation');
    }
  };

  const doAcceptRequest = async (req) => {
    setBusyId(req.id);
    try {
      await apiRequest('POST', '/accept-join', { request_id: req.id });
      showToast(`${req.full_name} ${t('accepted') || 'accepted'}`, 'user-check');
      loadPending();
      reload();
    } catch (err) {
      showToast(err.message ? (t(err.message) || err.message) : (t('errorOccurred') || 'Something went wrong'), 'exclamation');
    } finally {
      setBusyId(null);
    }
  };

  const doDeclineRequest = async (req) => {
    setBusyId(req.id);
    try {
      await apiRequest('POST', '/decline-join', { request_id: req.id });
      showToast(`${req.full_name} ${t('declined') || 'declined'}`, 'cross');
      loadPending();
    } catch (err) {
      showToast(err.message ? (t(err.message) || err.message) : (t('errorOccurred') || 'Something went wrong'), 'exclamation');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Screen p="3">
      <div className="top">
        <Back to="/team" />
        <Title>{t('players')}</Title>
        <TeamPaymentBanner lock={payLock} />

        {maxPlayers && (
          <div className="mb-3">
            <div style={styles.capacityBar}>
              <div style={styles.capacityFill(capacityPct)} />
            </div>
            <div style={styles.capacityCaption}>
              {players.length} / {maxPlayers}
            </div>
          </div>
        )}

        {/* INVITE — open seats on this team, the shareable link front and centre.
            Free-claim (already-bought team) or paid buy-a-seat, whichever applies.
            Hidden once the roster is full (all seats taken), even if the seat math
            still reports a spare. */}
        {!payLock && inviteOpen > 0 && inviteLink && (!maxPlayers || players.length < maxPlayers) && (
          <div style={styles.inviteCard}>
            <b>{inviteFree ? t('inviteFreeTitle') : t('inviteFriendsTitle')}</b>
            <div style={{ fontSize: '0.82rem', opacity: 0.9 }}>
              {t('seatsTaken', { taken: sales.seats_claimed, total: sales.seats_total })}
              {' · '}{inviteFree ? t('inviteFreeExplainer') : t('inviteCopyExplainer')}
            </div>
            <div style={styles.inviteRow}>
              <input readOnly value={inviteLink} style={styles.inviteInput}
                     onFocus={(e) => e.target.select()} />
              <button type="button" style={{ ...styles.addPill, background: 'rgba(255,255,255,0.22)' }}
                      onClick={copyInvite}>
                <Icon name="copy" theme="light" />
                {inviteCopied ? t('copied', { defaultValue: 'Copied!' }) : t('copyLink')}
              </button>
            </div>
            {/* Community leader: send one of these open seats to a specific person. */}
            {isContact && isCommunity && (
              <button type="button"
                      style={{ ...styles.addPill, marginTop: '0.5rem', background: 'rgba(255,255,255,0.22)' }}
                      onClick={openSendSeat}>
                <Icon name="paper-plane" theme="light" />
                {t('sendSeat')}
              </button>
            )}
          </div>
        )}

        <div style={styles.sectionLabel}>
          <span>{t('yourTeam') || 'Your roster'}</span>
        </div>

        {players.length > 0 ? (
          players.map((player) => {
            const isMe = player.id === userInfo?.user_id;
            return (
              <div key={player.id} style={styles.playerRow(player.is_captain)}>
                <Avatar url={player.photo_url} isCaptain={player.is_captain} />
                <div style={styles.nameBlock}>
                  <Link to={`/u/${player.id}`} style={styles.nameLink}>
                    <div style={styles.name}>
                      {player.full_name}
                      {player.is_captain && (
                        <span style={styles.captainPill}>
                          <Icon name="user-crown" pack="sr" theme="light" />
                          {t('captain') || 'Captain'}
                        </span>
                      )}
                      {isMe && <span style={styles.meTag}>({t('you') || 'you'})</span>}
                    </div>
                    {player.email && <div style={styles.email}>{player.email}</div>}
                  </Link>
                </div>

                {canModifyPlayers && !player.is_captain && (
                  <button
                    type="button"
                    style={styles.iconBtn('default')}
                    title={t('promoteToCaptain')}
                    disabled={busyId === player.id}
                    onClick={() => setConfirmPromote(player)}
                  >
                    <Icon name="user-crown" theme="primary" />
                  </button>
                )}
                {canModifyPlayers && !isMe && (!player.is_captain || captainCanLeave) && (
                  <button
                    type="button"
                    style={styles.iconBtn('default')}
                    title={t('replacePlayer')}
                    disabled={busyId === player.id}
                    onClick={() => openReplace(player)}
                  >
                    <Icon name="shuffle" theme="primary" />
                  </button>
                )}
                {canModifyPlayers && (!player.is_captain || captainCanLeave) && (
                  <button
                    type="button"
                    style={styles.iconBtn('danger')}
                    title={t('removePlayer')}
                    disabled={busyId === player.id}
                    onClick={() => setConfirmRemove(player)}
                  >
                    <Icon name="cross" theme="danger" />
                  </button>
                )}
                {canModifyPlayers && player.is_captain && !captainCanLeave && (
                  <button
                    type="button"
                    style={styles.iconBtn('danger')}
                    title={t('removePlayer')}
                    onClick={() => setCaptainLocked(true)}
                  >
                    <Icon name="cross" theme="danger" />
                  </button>
                )}
              </div>
            );
          })
        ) : (
          <div style={styles.emptyState}>
            {loading
              ? '…'
              : (t('noPlayersFound') || 'No players yet — add the first teammate below.')}
          </div>
        )}

        {canModifyPlayers && hasCompanyPool && (
        <div style={styles.sectionLabel}>
          <span>{t('addPlayers') || 'Add a teammate'}</span>
          {canCreateEmployee && (
            <button
              type="button"
              style={styles.createPill}
              onClick={shareInvite}
            >
              <Icon name="user-add" theme="primary" />
              {isCommunity ? t('addMember') : t('addColleague')}
            </button>
          )}
        </div>
        )}

        {/* The shareable invite link (fallback when clipboard is blocked, or just
            to review it). The joiner fills it in → a pending request the contact
            person approves below. */}
        {canModifyPlayers && hasCompanyPool && inviteShareLink && (
          <div style={styles.inviteCard}>
            <b>{isCommunity ? t('inviteMemberTitle') : t('inviteColleagueTitle')}</b>
            <div style={{ fontSize: '0.82rem', opacity: 0.9 }}>
              {isCommunity ? t('inviteMemberExplainer') : t('inviteColleagueExplainer')}
            </div>
            <div style={styles.inviteRow}>
              <input readOnly value={inviteShareLink} style={styles.inviteInput}
                     onFocus={(e) => e.target.select()} />
              <button type="button" style={{ ...styles.addPill, background: 'rgba(255,255,255,0.22)' }}
                      onClick={() => { navigator.clipboard.writeText(`${t('colleagueInviteShareText')}\n${inviteShareLink}`); setInviteShareCopied(true); setTimeout(() => setInviteShareCopied(false), 2500); }}>
                <Icon name="copy" theme="light" />
                {inviteShareCopied ? t('copied', { defaultValue: 'Copied!' }) : t('copyLink')}
              </button>
            </div>
          </div>
        )}

        {/* Pending join requests — the contact person accepts/declines. Only THEN
            does the person appear in the roster above. */}
        {isContact && pendingRequests.length > 0 && (
          <>
            <div style={styles.sectionLabel}>
              <span>{t('pendingRequests')}</span>
              <span style={styles.smallCount}>{pendingRequests.length}</span>
            </div>
            {pendingRequests.map((req) => (
              <div key={req.id} style={styles.playerRow(false)}>
                <Avatar url={null} isCaptain={false} />
                <div style={styles.nameBlock}>
                  <div style={styles.name}>{req.full_name}</div>
                  {req.email && <div style={styles.email}>{req.email}{req.team_name ? ` · ${req.team_name}` : ''}</div>}
                </div>
                <button type="button" style={styles.addPill} disabled={busyId === req.id}
                        onClick={() => doAcceptRequest(req)}>
                  <Icon name="check" theme="light" /> {t('accept')}
                </button>
                <button type="button" style={styles.iconBtn('danger')} disabled={busyId === req.id}
                        title={t('decline')} onClick={() => doDeclineRequest(req)}>
                  <Icon name="cross" theme="danger" />
                </button>
              </div>
            ))}
          </>
        )}

        {!canModifyPlayers ? (
          <div style={styles.fullBanner}>
            <Icon name="lock" theme="default" className="me-1" />
            {t('playerManagementClosed') || 'Roster changes are locked for this event.'}
          </div>
        ) : !hasCompanyPool ? (
          null /* consumer team: seats fill via the invite link above */
        ) : isFull ? (
          <div style={styles.fullBanner}>
            <Icon name="check" theme="success" className="me-1" />
            {t('teamFull') || 'Team is full — remove a player to add another.'}
          </div>
        ) : (
          <>
            <div style={styles.searchWrap}>
              <span style={styles.searchIcon}><Icon name="search" /></span>
              <input
                type="text"
                style={styles.searchInput}
                placeholder={t('searchPlayers') || 'Search colleagues…'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
              />
            </div>

            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((player) => (
                <div key={player.id} style={styles.playerRow(false)}>
                  <Avatar url={player.photo_url} isCaptain={false} />
                  <div style={styles.nameBlock}>
                    <Link to={`/u/${player.id}`} style={styles.nameLink}>
                      <div style={styles.name}>{player.full_name}</div>
                      {player.email && <div style={styles.email}>{player.email}</div>}
                    </Link>
                  </div>
                  <button
                    type="button"
                    style={styles.addPill}
                    disabled={busyId === player.id}
                    onClick={() => doAdd(player)}
                  >
                    <Icon name="plus" theme="light" />
                    {t('add') || t('addPlayer') || 'Add'}
                  </button>
                </div>
              ))
            ) : (
              <div style={styles.emptyState}>
                {employees.length === 0
                  ? (isCommunity ? t('noMembersAvailable') : t('noColleaguesAvailable'))
                  : (t('noMatches') || 'No matches.')}
              </div>
            )}
          </>
        )}

        {otherPlayers.length > 0 && (
          <>
            <div style={styles.sectionLabel}>
              <span>{t('unavailable') || 'On another team'}</span>
              <span style={styles.smallCount}>{otherPlayers.length}</span>
            </div>
            {otherPlayers.map((player) => (
              <div key={player.id} style={{ ...styles.playerRow(false), opacity: 0.65 }}>
                <Avatar url={player.photo_url} isCaptain={false} />
                <div style={styles.nameBlock}>
                  <Link to={`/u/${player.id}`} style={styles.nameLink}>
                    <div style={styles.name}>{player.full_name}</div>
                    {player.email && <div style={styles.email}>{player.email}</div>}
                  </Link>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Merge lives here now (moved from Edit team): merging is about the
            roster, not the team's name/photo. Gated with the other roster
            actions so a locked event hides it too. */}
        {canModifyPlayers && (
          <div style={{ marginTop: '1.5rem' }}>
            <Link to="/merge-teams" style={{ textDecoration: 'none', color: PRIMARY, fontWeight: 600 }}>
              <Icon name="users" theme="primary" className="me-1" /> {t('mergeTeams')} →
            </Link>
            <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.2rem' }}>
              {t('mergeTeamsHint')}
            </div>
          </div>
        )}
      </div>

      <div className="bottom">
        <div className="px-3">
          <hr className="mb-3" />
          <Button onClick={() => navigate(-1)} className="mb-3">
            {t('done')}
          </Button>
        </div>

        {/* Modals + Toast live in the bottom slot on purpose: Screen.js
            only renders children with className top/center/bottom, so
            anything outside those wrappers (Modal, Toast) gets silently
            dropped and the buttons that flip their `show` state appear
            to do nothing. */}
        <Modal
          show={!!confirmRemove}
          title={t('removePlayer')}
          onClose={() => setConfirmRemove(null)}
          onSubmit={() => confirmRemove && doRemove(confirmRemove)}
          submitText="remove"
        >
          {confirmRemove && (
            <p style={{ margin: 0 }}>
              {t('confirmRemovePlayer', { name: confirmRemove.full_name })}
            </p>
          )}
        </Modal>

        <Modal
          show={!!confirmReplace}
          title={t('replacePlayer')}
          onClose={() => setConfirmReplace(null)}
          onSubmit={replaceLink ? () => setConfirmReplace(null) : doReplace}
          submitText={replaceLink ? 'done' : 'sendInvite'}
        >
          {confirmReplace && !replaceLink && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <p style={{ margin: 0 }}>
                {t('replacePlayerIntro', { name: confirmReplace.full_name })}
              </p>
              <input
                type="email"
                style={{ ...styles.searchInput, paddingLeft: '0.9rem' }}
                placeholder={t('replacementEmail') || 'Replacement email'}
                value={replaceEmail}
                onChange={(e) => setReplaceEmail(e.target.value)}
                disabled={busyId === confirmReplace.id}
              />
              <input
                type="text"
                style={{ ...styles.searchInput, paddingLeft: '0.9rem' }}
                placeholder={t('firstNameOptional') || 'First name (optional)'}
                value={replaceFirst}
                onChange={(e) => setReplaceFirst(e.target.value)}
                disabled={busyId === confirmReplace.id}
              />
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#6b7280' }}>
                {t('replacePlayerNote')}
              </p>
            </div>
          )}
          {confirmReplace && replaceLink && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <p style={{ margin: 0 }}>
                {t('replaceSentIntro', { name: confirmReplace.full_name })}
              </p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  readOnly
                  style={{ ...styles.searchInput, paddingLeft: '0.9rem' }}
                  value={replaceLink}
                  onFocus={(e) => e.target.select()}
                />
                <button type="button" style={styles.createPill} onClick={copyReplaceLink}>
                  {replaceCopied ? (t('copied') || 'Copied') : (t('copyLink') || 'Copy link')}
                </button>
              </div>
            </div>
          )}
        </Modal>

        <Modal
          show={sendSeatOpen}
          title={t('sendSeatTitle')}
          onClose={() => setSendSeatOpen(false)}
          onSubmit={sendSeatLink ? () => setSendSeatOpen(false) : doSendSeat}
          submitText={sendSeatLink ? 'done' : 'sendInvite'}
        >
          {!sendSeatLink && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <p style={{ margin: 0 }}>{t('sendSeatIntro')}</p>
              <input
                type="email"
                style={{ ...styles.searchInput, paddingLeft: '0.9rem' }}
                placeholder={t('recipientEmail') || 'Their email'}
                value={sendSeatEmail}
                onChange={(e) => setSendSeatEmail(e.target.value)}
                disabled={sendSeatBusy}
              />
              <input
                type="text"
                style={{ ...styles.searchInput, paddingLeft: '0.9rem' }}
                placeholder={t('firstNameOptional') || 'First name (optional)'}
                value={sendSeatFirst}
                onChange={(e) => setSendSeatFirst(e.target.value)}
                disabled={sendSeatBusy}
              />
            </div>
          )}
          {sendSeatLink && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <p style={{ margin: 0 }}>{t('seatSentIntro')}</p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  readOnly
                  style={{ ...styles.searchInput, paddingLeft: '0.9rem' }}
                  value={sendSeatLink}
                  onFocus={(e) => e.target.select()}
                />
                <button type="button" style={styles.createPill} onClick={copySendSeatLink}>
                  {sendSeatCopied ? (t('copied') || 'Copied') : (t('copyLink') || 'Copy link')}
                </button>
              </div>
            </div>
          )}
        </Modal>

        <Modal
          show={!!confirmPromote}
          title={t('promoteToCaptain')}
          onClose={() => setConfirmPromote(null)}
          onSubmit={() => confirmPromote && doPromote(confirmPromote)}
          submitText="confirm"
        >
          {confirmPromote && (
            <p style={{ margin: 0 }}>
              {t('confirmPromoteCaptain', { name: confirmPromote.full_name })}
            </p>
          )}
        </Modal>

        <Modal
          show={captainLocked}
          title={t('cannotRemoveCaptain')}
          onClose={() => setCaptainLocked(false)}
          hideFooter
        >
          <p style={{ margin: '0 0 1rem' }}>{t('promoteNewCaptainFirst')}</p>
          <Button onClick={() => setCaptainLocked(false)} mb="0">
            {t('gotIt')}
          </Button>
        </Modal>

        <Toast
          showToast={toast.show}
          setShowToast={(s) => setToast((prev) => ({ ...prev, show: s }))}
          message={toast.message}
          icon={toast.icon}
        />
      </div>
    </Screen>
  );
};

export default Page;
